import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Play, ShieldCheck } from "lucide-react";
import { lineReveal } from "@/lib/motion";
import { IMAGES } from "@/data/content";

const HEADLINE = ["Accelerating Automotive", "Certification Through", "Expertise & Compliance"];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const contentY = useTransform(scrollYProgress, [0, 1], ["0%", "40%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section ref={ref} className="relative h-[100svh] min-h-[680px] overflow-hidden bg-[#0F172A]" data-testid="hero-section">
      {/* Parallax background image */}
      <motion.div style={{ y: imgY, scale: imgScale }} className="absolute inset-0 will-change-transform">
        <img
          src={IMAGES.hero}
          alt="Automotive certification testing laboratory"
          className="w-full h-full object-cover"
          loading="eager"
        />
      </motion.div>
      {/* Overlays */}
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 bg-gradient-to-r from-[#0F172A]/90 via-[#0F172A]/70 to-[#0F172A]/40" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A]/90 via-transparent to-[#0F172A]/30" />
      <div className="absolute inset-0 bg-grid-dark opacity-20" />

      {/* Content */}
      <motion.div style={{ y: contentY }} className="relative h-full max-w-[1320px] mx-auto px-5 sm:px-8 flex flex-col justify-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="inline-flex items-center gap-2 self-start rounded-full border border-white/20 bg-white/5 backdrop-blur-md px-4 py-2 mb-8"
        >
          <ShieldCheck className="w-4 h-4 text-blue-300" />
          <span className="text-white/90 text-xs sm:text-sm font-medium tracking-wide">
            Type Approval · Homologation · Regulatory Compliance in India
          </span>
        </motion.div>

        <h1 className="font-display font-bold text-white text-[2.6rem] sm:text-6xl lg:text-[5.2rem] leading-[1.02] tracking-tight max-w-5xl">
          {HEADLINE.map((line, i) => (
            <span key={i} className="reveal-mask">
              <motion.span className="block" variants={lineReveal} custom={i} initial="hidden" animate="visible">
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.9 }}
          className="mt-8 text-lg sm:text-xl text-slate-200/90 max-w-2xl leading-relaxed"
        >
          End-to-end certification, homologation, testing management and regulatory consulting
          services for India's automotive industry.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 1.1 }}
          className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
        >
          <Link
            to="/book-consultation"
            data-testid="hero-book-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#1E40AF] hover:bg-[#2563EB] text-white font-semibold px-8 py-4 text-base transition-colors shadow-lg shadow-blue-900/30 group"
          >
            Book a Consultation
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </Link>
          <Link
            to="/services"
            data-testid="hero-services-button"
            className="inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 backdrop-blur-md hover:bg-white/10 text-white font-semibold px-8 py-4 text-base transition-colors"
          >
            <Play className="w-4 h-4" />
            Explore Services
          </Link>
        </motion.div>
      </motion.div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-[10px] tracking-[0.3em] uppercase text-white/50">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-white/60 to-transparent" />
      </motion.div>
    </section>
  );
};

export default Hero;
